import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, unwrap } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { DataTable, type Column } from '../../components/DataTable';
import { ErrorState, LoadingRows } from '../../components/states';
import { StatusChip } from '../../components/StatusChip';
import { Field, Panel } from '../../components/ui';
import { date, dateTime, money } from '../../lib/format';
import { errorMessage, useNotify } from '../../lib/notify';
import { Callout, FieldGrid } from '../tenants/parts';
import type { TenantDetail } from '../tenants/api';
import {
  fetchTenantPlugin,
  PERIOD_LABELS,
  pluginKeys,
  PLUGINS,
  type PlatformPlugin,
  type PluginName,
  type PluginPeriod,
} from './api';

const STATE_LABEL: Record<PlatformPlugin['plugin']['state'], string> = {
  NOT_ENABLED: 'Not enabled',
  ACTIVE: 'Active',
  GRACE: 'In grace',
  EXPIRED: 'Ended',
};
const STATE_TONE: Record<PlatformPlugin['plugin']['state'], string> = {
  NOT_ENABLED: 'DRAFT',
  ACTIVE: 'ACTIVE',
  GRACE: 'GRACE',
  EXPIRED: 'EXPIRED',
};

const intIn = (v: string, min: number, max: number) => {
  if (!/^\d+$/.test(v.trim())) return null;
  const n = Number(v);
  return n >= min && n <= max ? n : null;
};

type Tenant = Pick<TenantDetail, 'id' | 'name'>;

/**
 * Tenant → a paid add-on (Foodpanda, Email), like the WhatsApp tab: enable / extend with the
 * period, its own grace days and the amount charged (it varies per client), change the grace, or
 * turn it off. Every change is kept in the history and counted in the add-on's revenue.
 */
export function PluginTenantTab({ tenant, plugin: name }: { tenant: Tenant; plugin: PluginName }) {
  const meta = PLUGINS[name];
  const Icon = meta.icon;
  const { can } = useAuth();
  const manage = can('subscriptions.manage');
  const q = useQuery({
    queryKey: pluginKeys.tenant(name, tenant.id),
    queryFn: () => fetchTenantPlugin(name, tenant.id),
  });
  const [dialog, setDialog] = useState<'extend' | 'grace' | 'disable' | 'units' | null>(null);

  if (q.isPending) return <LoadingRows rows={4} />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const { plugin, periods } = q.data;
  const enabled = plugin.state !== 'NOT_ENABLED';

  const columns: Column<PluginPeriod>[] = [
    { key: 'when', header: 'When', render: (p) => dateTime(p.createdAt) },
    { key: 'kind', header: 'Change', render: (p) => PERIOD_LABELS[p.kind] },
    {
      key: 'period',
      header: 'Period',
      render: (p) => (p.months ? `${String(p.months)} mo` : p.days ? `${String(p.days)} d` : '—'),
    },
    {
      key: 'through',
      header: 'Paid through',
      render: (p) => date(p.paidThroughAfter),
      hideBelow: 'sm',
    },
    { key: 'grace', header: 'Grace', render: (p) => `${String(p.graceDays)} d`, hideBelow: 'md' },
    {
      key: 'amount',
      header: 'Charged',
      align: 'right',
      render: (p) => (p.amount === null ? '—' : money(p.amount)),
    },
    { key: 'by', header: 'By', render: (p) => p.admin.name, hideBelow: 'md' },
    { key: 'notes', header: 'Notes', render: (p) => p.notes ?? '', hideBelow: 'lg' },
  ];

  return (
    <Stack spacing={2.5}>
      <Panel
        title={
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Icon sx={{ color: meta.color }} />
            <span>{meta.label} add-on</span>
            <StatusChip status={STATE_TONE[plugin.state]} label={STATE_LABEL[plugin.state]} />
          </Stack>
        }
        subtitle="Paid separately from the subscription. Hidden from the shop until enabled."
        action={
          manage && (
            <Stack direction="row" spacing={1}>
              {enabled && (
                <>
                  {name === 'email' && (
                    <Button
                      variant="outlined"
                      aria-label="Mail servers allowed"
                      onClick={() => setDialog('units')}
                    >
                      Mail servers
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    color="error"
                    aria-label={`Turn off ${meta.label}`}
                    onClick={() => setDialog('disable')}
                  >
                    Turn off
                  </Button>
                  <Button
                    variant="outlined"
                    aria-label={`${meta.label} grace`}
                    onClick={() => setDialog('grace')}
                  >
                    Grace
                  </Button>
                </>
              )}
              <Button
                variant="contained"
                aria-label={`${enabled ? 'Extend' : 'Enable'} ${meta.label}`}
                onClick={() => setDialog('extend')}
              >
                {enabled ? 'Extend' : 'Enable'}
              </Button>
            </Stack>
          )
        }
      >
        {enabled ? (
          <FieldGrid>
            <Field label="Paid through">{date(plugin.paidThrough)}</Field>
            <Field label="Grace">
              {plugin.graceDays ?? 0} days · until {date(plugin.graceEndsAt)}
            </Field>
            <Field label="Shop switch">
              {plugin.shopEnabled === false ? 'Switched off by the shop' : 'On'}
            </Field>
            <Field label="Charged in total">{money(plugin.totalCharged)}</Field>
            {name === 'email' && <Field label="Mail servers allowed">{plugin.units ?? 1}</Field>}
          </FieldGrid>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Not enabled for {tenant.name}.{' '}
            {Number(plugin.totalCharged) > 0 &&
              `Charged ${money(plugin.totalCharged)} before it was turned off.`}
          </Typography>
        )}
      </Panel>
      <Panel title={`${meta.label} history`} padded={false}>
        <DataTable
          columns={columns}
          rows={periods}
          rowKey={(p) => p.id}
          empty={<Box sx={{ p: 3, color: 'text.secondary' }}>No changes yet.</Box>}
        />
      </Panel>
      {dialog === 'extend' && (
        <ExtendDialog tenant={tenant} name={name} q={q.data} onClose={() => setDialog(null)} />
      )}
      {dialog === 'grace' && (
        <GraceDialog
          tenant={tenant}
          name={name}
          current={plugin.graceDays ?? 7}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === 'units' && (
        <UnitsDialog
          tenant={tenant}
          name={name}
          current={plugin.units ?? 1}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === 'disable' && (
        <DisableDialog tenant={tenant} name={name} onClose={() => setDialog(null)} />
      )}
    </Stack>
  );
}

function useDone(name: PluginName, tenantId: string, onClose: () => void) {
  const qc = useQueryClient();
  const notify = useNotify();
  return (msg: string) => {
    notify.success(msg);
    void qc.invalidateQueries({ queryKey: pluginKeys.tenant(name, tenantId) });
    void qc.invalidateQueries({ queryKey: pluginKeys.revenue(name) });
    onClose();
  };
}

function ExtendDialog({
  tenant,
  name,
  q,
  onClose,
}: {
  tenant: Tenant;
  name: PluginName;
  q: PlatformPlugin;
  onClose: () => void;
}) {
  const label = PLUGINS[name].label;
  const done = useDone(name, tenant.id, onClose);
  const enabled = q.plugin.state !== 'NOT_ENABLED';
  const [key] = useState(() => crypto.randomUUID());
  const [unit, setUnit] = useState<'months' | 'days'>('months');
  const [length, setLength] = useState('1');
  const [grace, setGrace] = useState(String(q.plugin.graceDays ?? 7));
  const [amount, setAmount] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const n = intIn(length, 1, unit === 'months' ? 60 : 1830);
  const g = intIn(grace, 0, 60);
  const amountValue = amount ?? '';
  const amountOk = /^\d+(\.\d{1,2})?$/.test(amountValue.trim());

  const submit = () => {
    if (n === null || g === null || !amountOk) return;
    setBusy(true);
    setError(null);
    unwrap(
      api.POST('/platform/tenants/{id}/plugins/{plugin}/extend', {
        params: { path: { id: tenant.id, plugin: name }, header: { 'idempotency-key': key } },
        body: {
          ...(unit === 'months' ? { months: n } : { days: n }),
          graceDays: g,
          amount: amountValue.trim(),
          currency: 'PKR',
          ...(notes.trim() ? { notes: notes.trim() } : {}),
        },
      }),
    )
      .then((r) => done(`${label} paid through ${date(r.plugin.paidThrough)}`))
      .catch((e: unknown) => {
        setBusy(false);
        setError(e);
      });
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {enabled ? `Extend ${label}` : `Enable ${label}`}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.25} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {enabled
              ? `${tenant.name} is paid through ${date(q.plugin.paidThrough)}. The period is added from that date, or from today if it has passed.`
              : `Starts today for ${tenant.name}. The shop sees ${label} straight away.`}
          </Typography>
          {error !== null && <Callout severity="error">{errorMessage(error)}</Callout>}
          <Stack direction="row" spacing={1.5}>
            <TextField
              label="Period"
              value={length}
              onChange={(e) => setLength(e.target.value)}
              error={n === null}
              slotProps={{ htmlInput: { inputMode: 'numeric' } }}
              sx={{ flex: 1 }}
            />
            <TextField
              select
              label="Unit"
              value={unit}
              onChange={(e) => setUnit(e.target.value as 'months' | 'days')}
              sx={{ width: 130 }}
            >
              <MenuItem value="months">Months</MenuItem>
              <MenuItem value="days">Days</MenuItem>
            </TextField>
          </Stack>
          <TextField
            label="Grace days"
            value={grace}
            onChange={(e) => setGrace(e.target.value)}
            error={g === null}
            helperText="0–60. Counted from the end of the paid period."
            slotProps={{ htmlInput: { inputMode: 'numeric' } }}
          />
          <TextField
            label="Amount charged (PKR)"
            value={amountValue}
            onChange={(e) => setAmount(e.target.value)}
            error={!amountOk}
            helperText="Enter what this client paid."
            autoFocus
            slotProps={{ htmlInput: { inputMode: 'decimal' } }}
          />
          <TextField
            label="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            minRows={2}
            slotProps={{ htmlInput: { maxLength: 500 } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button variant="outlined" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          loading={busy}
          disabled={n === null || g === null || !amountOk}
          onClick={submit}
        >
          {enabled ? 'Extend' : 'Enable'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function GraceDialog({
  tenant,
  name,
  current,
  onClose,
}: {
  tenant: Tenant;
  name: PluginName;
  current: number;
  onClose: () => void;
}) {
  const label = PLUGINS[name].label;
  const done = useDone(name, tenant.id, onClose);
  const [grace, setGrace] = useState(String(current));
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const g = intIn(grace, 0, 60);
  const submit = () => {
    if (g === null) return;
    setBusy(true);
    unwrap(
      api.POST('/platform/tenants/{id}/plugins/{plugin}/grace', {
        params: { path: { id: tenant.id, plugin: name } },
        body: { graceDays: g, ...(notes.trim() ? { notes: notes.trim() } : {}) },
      }),
    )
      .then(() => done(`${label} grace set to ${String(g)} days`))
      .catch((e: unknown) => {
        setBusy(false);
        setError(e);
      });
  };
  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{label} grace</DialogTitle>
      <DialogContent>
        <Stack spacing={2.25} sx={{ pt: 1 }}>
          {error !== null && <Callout severity="error">{errorMessage(error)}</Callout>}
          <TextField
            label="Grace days"
            value={grace}
            onChange={(e) => setGrace(e.target.value)}
            error={g === null}
            helperText={`Replaces the current ${String(current)} days, counted from the end of the paid period (5 → 7 adds 2 days).`}
          />
          <TextField
            label="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button variant="outlined" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" loading={busy} disabled={g === null} onClick={submit}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Email: how many outgoing mail servers the shop may set up (newest ones beyond it stop sending). */
function UnitsDialog({
  tenant,
  name,
  current,
  onClose,
}: {
  tenant: Tenant;
  name: PluginName;
  current: number;
  onClose: () => void;
}) {
  const done = useDone(name, tenant.id, onClose);
  const [units, setUnits] = useState(String(current));
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const u = intIn(units, 1, 20);
  const submit = () => {
    if (u === null) return;
    setBusy(true);
    unwrap(
      api.POST('/platform/tenants/{id}/plugins/{plugin}/units', {
        params: { path: { id: tenant.id, plugin: name } },
        body: { units: u, ...(notes.trim() ? { notes: notes.trim() } : {}) },
      }),
    )
      .then(() =>
        done(`${tenant.name} may have ${String(u)} mail ${u === 1 ? 'server' : 'servers'}`),
      )
      .catch((e: unknown) => {
        setBusy(false);
        setError(e);
      });
  };
  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Mail servers allowed</DialogTitle>
      <DialogContent>
        <Stack spacing={2.25} sx={{ pt: 1 }}>
          {error !== null && <Callout severity="error">{errorMessage(error)}</Callout>}
          <TextField
            label="Mail servers"
            value={units}
            onChange={(e) => setUnits(e.target.value)}
            error={u === null}
            helperText={
              u !== null && u < current
                ? 'The shop’s newest servers beyond this number stop sending until it removes some.'
                : 'How many outgoing mail servers the shop may set up (1–20), e.g. one for sales and one for accounts.'
            }
            slotProps={{ htmlInput: { inputMode: 'numeric' } }}
          />
          <TextField
            label="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button variant="outlined" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" loading={busy} disabled={u === null} onClick={submit}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function DisableDialog({
  tenant,
  name,
  onClose,
}: {
  tenant: Tenant;
  name: PluginName;
  onClose: () => void;
}) {
  const { label, hidden } = PLUGINS[name];
  const done = useDone(name, tenant.id, onClose);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const submit = () => {
    setBusy(true);
    unwrap(
      api.POST('/platform/tenants/{id}/plugins/{plugin}/disable', {
        params: { path: { id: tenant.id, plugin: name } },
        body: notes.trim() ? { notes: notes.trim() } : {},
      }),
    )
      .then(() => done(`${label} turned off`))
      .catch((e: unknown) => {
        setBusy(false);
        setError(e);
      });
  };
  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Turn off {label}?</DialogTitle>
      <DialogContent>
        <Stack spacing={2.25} sx={{ pt: 1 }}>
          {error !== null && <Callout severity="error">{errorMessage(error)}</Callout>}
          <Typography variant="body2">
            {hidden} for {tenant.name}. Enabling it later starts a new period from that day.
          </Typography>
          <TextField
            label="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button variant="outlined" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" color="error" loading={busy} onClick={submit}>
          Turn off
        </Button>
      </DialogActions>
    </Dialog>
  );
}
